import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  IPatient,
  MentionResourceType,
  PatientReferrer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  WithRef,
  getError,
  toNamedDocument,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BasePatientRelationshipDestinationEntity,
  IPatientRelationshipJobData,
} from '../../../destination/entities/patient-relationship';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { TranslationMapHandler } from '../../../translation-map';
import { PATIENT_NUMBER_LIMIT } from '../../source/entities/third-parties';
import {
  IOasisPatient,
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PatientDestinationEntity } from './patients';

export const PATIENT_REFERRER_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'patientReferrers',
    label: 'Patient Referrers',
    description: '',
  },
});

export interface IPatientReferrerDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  patientRef: DocumentReference<IPatient>;
  referrerRef: DocumentReference<IPatient>;
}

export type IPatientReferrerJobData = IPatientRelationshipJobData<OasisPatient>;

export interface IPatientReferrerMigrationData {
  patientRef: DocumentReference<IPatient>;
  referrer: PatientReferrer;
  referrerRef: DocumentReference<IPatient>;
}

export class PatientReferrerDestinationEntity extends BasePatientRelationshipDestinationEntity<
  OasisPatient,
  IPatientReferrerDestinationRecord,
  IPatientReferrerMigrationData
> {
  destinationEntity = PATIENT_REFERRER_DESTINATION_ENTITY;

  patientSourceEntity = new PatientSourceEntity();
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  getMigratedData$(
    record: IDestinationEntityRecord<IPatientReferrerDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return from(Firestore.getDoc(record.data.referrerRef)).pipe(
      map((patient) => [
        {
          label: 'Referrer',
          data: patient,
        },
      ])
    );
  }

  async buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientReferrerJobData
  ): Promise<
    | IPatientReferrerMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    const patient = data.sourcePatient.data;
    const sourcePatientId = this.sourceEntities.patients
      .getSourceRecordId(patient)
      .toString();

    if (!hasReferrer(patient)) {
      return this.buildSkippedResponse(data);
    }

    try {
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );
      if (!patientRef) {
        throw new Error(
          `Patient destination not found for id ${sourcePatientId}`
        );
      }

      const referralPatientId = patient.referralPatientId.toString();
      const referrerRef = await translationMap.getDestination<IPatient>(
        referralPatientId,
        PATIENT_RESOURCE_TYPE
      );
      if (!referrerRef) {
        throw new Error(
          `Referrer patient destination not found for referralPatientId ${referralPatientId} on patient ${sourcePatientId}`
        );
      }

      const referrerPatient = await Firestore.safeGetDoc(referrerRef);
      if (!referrerPatient) {
        throw new Error(
          `Referrer patient document not found for referralPatientId ${referralPatientId} on patient ${sourcePatientId}`
        );
      }

      return {
        patientRef,
        referrerRef,
        referrer: {
          ...toNamedDocument(referrerPatient),
          type: MentionResourceType.Patient,
        },
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMapHandler: TranslationMapHandler,
    jobData: IPatientReferrerJobData,
    migrationData: IPatientReferrerMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      await FirestoreMigrate.patchDoc(migrationData.patientRef, {
        referrer: migrationData.referrer,
      });

      return this._buildSuccessResponse(
        jobData.sourcePatient.record,
        migrationData.patientRef,
        migrationData.referrerRef
      );
    } catch (error) {
      return this._buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _buildSuccessResponse(
    sourcePatient: Pick<
      IGetRecordResponse<OasisPatient>['record'],
      'label' | 'uid' | 'id'
    >,
    patientRef: DocumentReference<IPatient>,
    referrerRef: DocumentReference<IPatient>
  ): IDestinationEntityRecord<IPatientReferrerDestinationRecord> {
    return {
      uid: sourcePatient.uid,
      label: sourcePatient.label,
      data: {
        sourceRef: sourcePatient.id,
        patientRef,
        referrerRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.id,
      migratedAt: toTimestamp(),
    };
  }
}

type OasisPatientWithReferrer = IOasisPatient & { referralPatientId: number };

/**
 * A patient has a resolvable patient referrer when `referralPatientId` is set,
 * is not a self-reference, and is not a third-party contact id (>= the patient
 * number limit).
 */
function hasReferrer(
  patient: IOasisPatient
): patient is OasisPatientWithReferrer {
  if (!patient.referralPatientId) {
    return false;
  }
  const isSelfReferral = patient.referralPatientId === patient.id;
  const isContactId = patient.referralPatientId >= PATIENT_NUMBER_LIMIT;
  return !isSelfReferral && !isContactId;
}
